import type { PositioningMethod } from "building-types/annotation/annotationTypesEnum";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import type { DataTable } from "building-types/dataTables/dataTable";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { DocEntityId } from "building-types/doc/docEntityId";
import type { DocForeignKeyTable } from "building-types/doc/docForeignKeyTable";
import type { ForeignKeyPathOf } from "building-types/doc/foreignKeyPath";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { RemoveFormula } from "building-types/options/formula";
import type {
  ParameterRef,
  ParameterValueHandle,
  RemoveParameterRef,
  RemoveParameterValueHandle,
} from "building-types/options/parameter";
import type { ParameterFormulaDisabled } from "building-types/options/parameterFormulaDisabled";
import type { PlanLineStyle } from "building-types/settings/settingsTypesEnum";
import type { ViewportParameterValues } from "building-types/systemParameterTypes";
import type { GradeLineSource } from "building-types/views/GradeLineSource";
import type { SelectionMethod } from "building-types/views/SerializableSheetGeometrySelector";
import type { Table } from "common/data/table";
import type { LevelInches, SheetInches } from "common/types/nominalNumbersTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { BuiltinPlanViewType } from "./BuiltinPlanViewType";
import type { ViewportCameraKey } from "./CameraKey";
import type { ElevationAnnotationParams } from "./elevationAnnotationTypes";
import type { DocViewType } from "./GeneratedPageView";
import type { OptionPresetBase } from "./optionPresetTypes";
import type { LineStyle, TextStyle } from "./Primitive2DOutput";
import type { SheetGeometryCategoryTable } from "./sheetGeometryCategory";
import type { SheetGeometryCategory } from "./sheetGeometryCategory";
import { BUILT_IN_VIEW_SCALE_1_PER_4_INCH } from "./viewScaleConstants";
import type { ViewStyle } from "./ViewStyle";
import type { DimensionPrecision } from "./viewTypes";

/**
 * Once evaluated, a parameter ref will be replaced with the value of the parameter.
 * see {@link RemoveParameterRef}
 */
export type ParameterRefStringField = ParameterRef | string;
export type ParameterRefNumberField = ParameterRef | number;

export type PlanSetTemplateType = "BUILT_IN" | "CUSTOM";

interface BasePlanSetTemplateIdentifier {
  readonly type: PlanSetTemplateType;
}

export interface BuiltInPlanSetTemplateIdentifier extends BasePlanSetTemplateIdentifier {
  readonly type: "BUILT_IN";
  readonly templateId?: never;
  readonly templateConfigId?: never;
}

export const defaultPlanSetTemplateIdentifier: BuiltInPlanSetTemplateIdentifier = { type: "BUILT_IN" };

export const builtInPlanSetTemplateIdLabels: { [key in BuiltInPlanSetTemplateIdentifier["type"]]: string } = {
  BUILT_IN: "Built-in Lot-Specific",
};

export interface CustomPlanSetTemplateIdentifier extends BasePlanSetTemplateIdentifier {
  readonly type: "CUSTOM";
  readonly templateId: ForeignEntityId<"PLAN_SET_TEMPLATE">;
  readonly templateConfigId?: ForeignEntityId<"PLAN_SET_TEMPLATE_CONFIG">;
}

export type PlanSetTemplateIdentifier = BuiltInPlanSetTemplateIdentifier | CustomPlanSetTemplateIdentifier;

/**
 * PlanSet is the root entity for a plan set. It contains a list of sheet template instances.
 */
export interface PlanSetTemplate extends DocEntityBase<"PLAN_SET_TEMPLATE"> {
  readonly id: PrimaryEntityId<"PLAN_SET_TEMPLATE">;
  readonly exportPermissions: PlanSetTemplateExportPermissions;
  // Children:
  // - SheetTemplate instances (type: "SHEET")
  // - Parameters
  // - PlanSetTemplateConfig
}

export interface PlanSetTemplateConfig extends DocEntityBase<"PLAN_SET_TEMPLATE_CONFIG"> {
  readonly id: PrimaryEntityId<"PLAN_SET_TEMPLATE_CONFIG">;
  readonly parentId?: DocEntityParentId<"PLAN_SET_TEMPLATE">;
  readonly parameterValues: DocForeignKeyTable<"LOCAL_PARAMETER" | "SYSTEM_PARAMETER", ParameterValueHandle>;
  readonly globalParameterValues: DocForeignKeyTable<"GLOBAL_PARAMETER", ParameterValueHandle>;
}

export type ParameterValuesEntityField = keyof Pick<PlanSetTemplateConfig, "parameterValues" | "globalParameterValues">;

/**
 * Export permissions keyed by role name with values indicating permission level
 */
export type PlanSetTemplateExportPermissions = Table<"ALLOW" | "DENY" | "IF_ASSIGNED">;

/**
 * Note that tags should only be applied to drawing templates, not sheet templates.
 */
interface ContentTemplateBase extends LabelableEntityBase, DocEntityBase<"CONTENT_TEMPLATE"> {
  readonly id: PrimaryEntityId<"CONTENT_TEMPLATE">;
  // Children:
  // - ContentTemplateInstance
  // - Annotation
  // - Viewport
  // - Parameter
  // - Table
}

/**
 * A sheet template is a template for a sheet in a plan set. It contains a margin and has child entities
 * that may be viewports, annotation, content template instances, or tables.
 */
export interface SheetTemplate extends ContentTemplateBase {
  readonly templateType: "SHEET";
  readonly margin: number;
  readonly width: number;
  readonly height: number;
}

interface DrawingTemplateBase extends ContentTemplateBase {
  readonly templateType: "DRAWING";
}

interface ScaledDrawingTemplateBase extends DrawingTemplateBase {
  readonly viewScale: number;
}

/** Allows hosting of any possible child types */
export interface GenericDrawingTemplate extends ScaledDrawingTemplateBase {
  readonly drawingType?: undefined; // TODO: set an actual value for this and add a migration
}

/**
 * Can be used in a model context, as well as inside other content templates or viewports.
 * Allows hosting annotations and other annotation drawing templates
 * */
export interface AnnotationDrawingTemplate extends ScaledDrawingTemplateBase {
  readonly drawingType: "ANNOTATION";
}

/** Allows hosting of a restricted set of child types depending on the drawing type */
export interface ViewportTitleDrawingTemplate extends DrawingTemplateBase, Partial<ViewportParameterValues> {
  readonly drawingType: "VIEWPORT_TITLE";
}

export interface TagTemplate extends DrawingTemplateBase {
  readonly drawingType: "TAG";
  readonly targetEntityType: "ROOM";
}

export type DrawingTemplate =
  | GenericDrawingTemplate
  | AnnotationDrawingTemplate
  | ViewportTitleDrawingTemplate
  | TagTemplate;

export type ContentTemplate = SheetTemplate | DrawingTemplate;

/**
 * Places a content template instance in a particular position.
 */
export interface ContentTemplateInstanceBase
  extends DocEntityBase<"CONTENT_TEMPLATE_INSTANCE">,
    LabelableEntityBase, // Only for drawing template instances that have been migrated from annotation templates.
    ParameterFormulaDisabled {
  readonly id: PrimaryEntityId<"CONTENT_TEMPLATE_INSTANCE">;
  readonly contentTemplateId: DocEntityId<"CONTENT_TEMPLATE">;
  readonly parameterValues: DocForeignKeyTable<
    "LOCAL_PARAMETER" | "SYSTEM_PARAMETER" | "GLOBAL_PARAMETER",
    ParameterValueHandle | ParameterRef
  >;
  readonly parentId?: DocEntityParentId<
    | "CONTENT_TEMPLATE"
    | "PLAN_SET_TEMPLATE"
    | "CONTENT_TEMPLATE_INSTANCE"
    | "LEVEL"
    | "ROOM"
    | "ROOM_LAYOUT"
    | "ELEVATION_VIEW"
    | "SECTION_VIEW"
  >;
}

export interface SheetTemplateInstance extends ContentTemplateInstanceBase {
  readonly templateType: "SHEET";
  readonly sheetNumberPrefix: string;
  readonly sheetNumber: string;
}

/**
 * A drawing template instance placed in a plan set template or view.
 */
export interface DrawingTemplateInstance extends ContentTemplateInstanceBase {
  readonly templateType: "DRAWING";
  readonly x: number;
  readonly y: number;
  readonly rotation: number; // in degrees
  readonly positioningMethod: PositioningMethod;
  readonly edgeId?: string;
}

export type ContentTemplateInstance = SheetTemplateInstance | DrawingTemplateInstance;

/**
 * Content replicators will allow for the creation of multiple instances of a content template.
 * This type is also not really fleshed out yet.
 */
export interface ContentReplicator extends DocEntityBase<"CONTENT_REPLICATOR">, ParameterFormulaDisabled {
  readonly id: PrimaryEntityId<"CONTENT_REPLICATOR">;
  readonly template: ContentTemplate;
  readonly sequenceId: ForeignEntityId<"DATA_SEQUENCE">; // e.g. levels in building
  readonly parentId?: DocEntityParentId<"CONTENT_TEMPLATE">;
}

export enum ViewportHorizontalAlignment {
  LEFT = "LEFT",
  CENTER = "CENTER",
  RIGHT = "RIGHT",
}

export enum ViewportVerticalAlignment {
  TOP = "TOP",
  CENTER = "CENTER",
  BOTTOM = "BOTTOM",
}

export enum ViewportPositioningMethod {
  ALIGNED = "ALIGNED",
  EXPLICIT = "EXPLICIT",
}

export enum ViewportScalingMethod {
  EXPLICIT = "EXPLICIT",
  FIT = "FIT",
}

export interface ViewportPosition {
  // where is the world origin on the page?
  readonly worldOriginInPageSpaceX: SheetInches;
  readonly worldOriginInPageSpaceY: SheetInches;

  readonly outline: PolyLine2;

  readonly scale?: ParameterRefNumberField;

  readonly positioningMethod?: ViewportPositioningMethod;
  readonly scalingMethod?: ViewportScalingMethod;

  readonly verticalAlignment?: ViewportVerticalAlignment;
  readonly horizontalAlignment?: ViewportHorizontalAlignment;
}

export const DEFAULT_VIEWPORT_SCALE = BUILT_IN_VIEW_SCALE_1_PER_4_INCH;

interface EvaluatedOptionSetOverrides {
  readonly optionSetOverrides?: Evaluated<OptionPresetBase["optionSetOverrides"]>;
}

export type EvaluatedWithOptionSetOverrides<T extends OptionPresetBase> = Evaluated<
  Omit<T, "optionSetOverrides"> & EvaluatedOptionSetOverrides
>;

export type ViewportViewType = DocViewType | "DRAWING";

interface ViewTypeBase {
  readonly viewType: ViewportViewType;
}

export interface ViewTemplateOverrideParent {
  readonly viewTemplateOverrideIds: DocEntityId<"VIEW_TEMPLATE_OVERRIDE">[];
}

/**
 * Allows placement of a view on a sheet.
 */
export interface ViewportBase
  extends ViewTypeBase,
    ViewTemplateOverrideParent,
    ViewportPosition,
    DocEntityBase<"VIEWPORT">,
    ParameterFormulaDisabled {
  readonly id: PrimaryEntityId<"VIEWPORT">;
  readonly parentId?: DocEntityParentId<"CONTENT_TEMPLATE" | "CONTENT_TEMPLATE_INSTANCE">;
  readonly viewTemplateId: ParameterRef | DocEntityId<"VIEW_TEMPLATE">;
  readonly contentTextScale?: number;
  readonly dimensionPrecision?: DimensionPrecision;
  readonly title?: ParameterRefStringField;
  readonly number?: ParameterRefStringField;
  readonly description?: ParameterRefStringField;
  readonly titleDrawingTemplateId?: DocEntityId<"CONTENT_TEMPLATE">;
  readonly selectedReportingUnitLabelId?: ParameterRef | ForeignEntityId<"LABEL">;
}

export interface PlanViewport extends ViewportBase, OptionPresetBase {
  readonly viewType: "PLAN_VIEW";
  readonly contentSourceId: ParameterRef | DocEntityId<"LEVEL"> | DocEntityId<"COMPOSITE_LEVEL">;
  readonly contentSourceComponentInstanceChain?: ForeignKeyPathOf<"COMPONENT_INSTANCE">;
  readonly contentRotationDegrees?: ParameterRefNumberField;

  // Relaxation controls
  readonly iterationStep?: number;
  readonly maxIterationDistance?: number;
}

export type ElevationViewport = ViewportBase &
  OptionPresetBase & {
    readonly viewType: "ELEVATION_VIEW";
    readonly contentSourceId: ParameterRef | DocEntityId<"ELEVATION_VIEW">;
    readonly elevationAnnotationParams?: ElevationAnnotationParams;
  };

export type SectionViewport = ViewportBase &
  OptionPresetBase & {
    readonly viewType: "SECTION_VIEW";
    readonly contentSourceId: ParameterRef | DocEntityId<"SECTION_VIEW">;
  };

export type PerspectiveViewport = ViewportBase &
  OptionPresetBase & {
    readonly viewType: "VIEW_3D";
    readonly contentSourceId: ParameterRef | ViewportCameraKey;
  };

export type DrawingViewport = ViewportBase & {
  readonly viewType: "DRAWING";
  readonly contentSourceId: ParameterRef | DocEntityId<"CONTENT_TEMPLATE">;
  readonly parameterValues: DocForeignKeyTable<
    "LOCAL_PARAMETER" | "SYSTEM_PARAMETER" | "GLOBAL_PARAMETER",
    ParameterValueHandle | ParameterRef
  >;
  readonly contentRotationDegrees?: ParameterRefNumberField;
};

export type ViewportRenderStyle = "default" | "showroomFloorPlan";

export type Viewport = PlanViewport | ElevationViewport | SectionViewport | PerspectiveViewport | DrawingViewport;

interface EvaluatedParameterValuesTable {
  readonly parameterValues: Evaluated<DrawingViewport["parameterValues"]>;
}

export type EvaluatedDrawingViewport = Evaluated<Omit<DrawingViewport, "parameterValues">> &
  EvaluatedParameterValuesTable;

export type EvaluatedViewport =
  | EvaluatedWithOptionSetOverrides<PlanViewport>
  | EvaluatedWithOptionSetOverrides<ElevationViewport>
  | EvaluatedWithOptionSetOverrides<SectionViewport>
  | EvaluatedWithOptionSetOverrides<PerspectiveViewport>
  | EvaluatedDrawingViewport;

export interface SheetGeometryCategoryStyle {
  line?: LineStyle;
  text?: TextStyle;
  [key: string]: LineStyle | TextStyle | undefined;
}

// TODO: This is a temporary workaround until LineStyle and TextStyle are moved to the `common` package.
type SheetGeometryStyleTable = SheetGeometryCategoryTable<SheetGeometryCategoryStyle>;

export enum RoomTagCapitalization {
  UPPER_CASE = "UPPER_CASE",
  TITLE_CASE = "TITLE_CASE",
  DEFAULT = "DEFAULT", // DEFAULT capitalization does not apply any changes to the raw text
}

export interface ViewTemplateBase extends ViewTypeBase, ViewTemplateOverrideParent, DocEntityBase<"VIEW_TEMPLATE"> {
  readonly id: PrimaryEntityId<"VIEW_TEMPLATE">;
  readonly categories: readonly SheetGeometryCategory[];
  readonly styleOverrides: SheetGeometryStyleTable;
  readonly viewStyle?: ViewStyle;
}

export interface PlanViewTemplate extends ViewTemplateBase {
  readonly viewType: "PLAN_VIEW";
  readonly referenceType?: BuiltinPlanViewType;
  readonly reduceOverlap?: boolean;
  readonly roomTagTemplateId?: DocEntityId<"CONTENT_TEMPLATE">;
  readonly roomTagCapitalization?: RoomTagCapitalization;
  /** Inches above floor elevation at which the plan is cut. Defaults to FLOOR_PLAN_CUT_PLANE (48). */
  readonly cutPlaneHeight?: LevelInches;
  /**
   * Inches above floor elevation at which stairs are cut. Defaults to STAIR_CUT_HEIGHT when unset.
   */
  readonly stairCutPlaneHeight?: LevelInches;
  /** Inches above floor elevation at which exterior wall finishes are cut. Falls back to cutPlaneHeight when unset. */
  readonly wallFinishCutPlaneHeight?: LevelInches;
}

export interface SectionViewTemplate extends ViewTemplateBase {
  readonly viewType: "SECTION_VIEW";
  /**
   * Where the grade line's geometry comes from for viewports built on this template. Independent of
   * `SectionView.gradeLineSource`, which governs the view's own viewport -- the same split as
   * grade line visibility, where a template uses `categories` and a view uses `viewFilter.gradeLine`.
   * Gated on the `modeledGradeLine` flag at render time for the same reason as that field.
   */
  readonly gradeLineSource?: GradeLineSource;
}

export interface ElevationViewTemplate extends ViewTemplateBase {
  readonly viewType: "ELEVATION_VIEW";
  readonly belowGradeRenderStyle?: PlanLineStyle;
  /**
   * Where the grade line's geometry comes from for viewports built on this template. Independent of
   * `ElevationView.gradeLineSource`, which governs the view's own viewport -- the same split as
   * grade line visibility, where a template uses `categories` and a view uses `viewFilter.gradeLine`.
   * Gated on the `modeledGradeLine` flag at render time for the same reason as that field.
   */
  readonly gradeLineSource?: GradeLineSource;
}

export interface PerspectiveViewTemplate extends ViewTemplateBase {
  readonly viewType: "VIEW_3D";
}

export interface DrawingViewTemplate extends ViewTemplateBase {
  readonly viewType: "DRAWING";
}

export type ViewTemplate =
  | PlanViewTemplate
  | SectionViewTemplate
  | ElevationViewTemplate
  | PerspectiveViewTemplate
  | DrawingViewTemplate;

// TODO (paulscrugham): labels should not be directly added to view template overrides (or other entity types for that matter).
// a link between a label and a view template override should be established in a separate "join" doc table.
export interface ViewTemplateOverride extends LabelableEntityBase, DocEntityBase<"VIEW_TEMPLATE_OVERRIDE"> {
  readonly id: PrimaryEntityId<"VIEW_TEMPLATE_OVERRIDE">;
  readonly selectionMethod: SelectionMethod;
}

export const WRAPPER_CONTENT_TEMPLATE_INSTANCE_ID = "WRAPPER_CONTENT_TEMPLATE_INSTANCE_ID";

export const LevelFilterableKAMTypes: SystemDataSequenceEntityType[] = ["ELECTRICAL"];

export const DEFAULT_ROOM_TAG_CAPITALIZATION = RoomTagCapitalization.UPPER_CASE;

export type EvaluatedDataTable = EvaluatedWithOptionSetOverrides<DataTable>;

export type Evaluated<T> = RemoveParameterRef<RemoveFormula<RemoveParameterValueHandle<T>>>;
